/* global G, PLATFORM */
import asObject from 'lib/sequence/component/children/asObject'
import sequenceComponentFind from 'lib/sequence/component/children/find'
import { arrayOf, asyncPipeSpread, isNum } from 'lib/util'
import state from 'lib/sequence/component/state'

const { set: setActiveStep } = state('activeStep')
const { set: complete } = state('completed')
const { set: confirm } = state('confirmed')

const operations = { confirm, complete }

/**
 * Sets the steppers `activeStep` based on incoming data.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const presetActiveStep = module => async (components, detail = {}, ...args) => {
  const { stepper } = components;

  (!!detail?.step || detail?.step === 0) && setActiveStep(stepper, detail.step)

  return [components, detail, ...args]
}

/**
 * Presets the steps based on the incoming data and the value of `operation`.
 *
 * @param {'confirm'|'complete'} operation the operation to perform on the steps.
 * @returns {function(*): function(*, *, ...[*]): Promise<*>}
 */
const presetSteps = operation => module => async (components, detail = {}, ...args) => {
  const { stepper } = components
  const steps = stepper[G.CHILDREN]

  arrayOf(detail[operation]).forEach((confirmedStep) => {
    operations[operation](
      isNum(confirmedStep)
        ? steps[confirmedStep]
        : asObject(steps)[confirmedStep],
    )
  })

  return [components, detail, ...args]
}

const presetConfirmSteps = presetSteps('confirm')
const presetCompleteSteps = presetSteps('complete')

/**
 * Presets steps of the stepper depending on incoming data.
 *
 * The `activeStep` may be set by the `step` argument.
 * The confirmed steps may be set by the `confirm` argument.
 * The completed steps may be set by the `complete` argument.
 *
 * @example
 * {
 *   step: 1 // or 'device',
 *   confirm: 0 // or 'device' or [0, 1] or ['device', 'location']
 *   complete: 0 // or 'device' or [0, 1] or ['device', 'location']
 * }
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {function(...[*]): Promise<*>}
 */
export const presetStepper = module => async (...args) => asyncPipeSpread(
  presetActiveStep(module),
  presetConfirmSteps(module),
  presetCompleteSteps(module),
)(...args)

/**
 * Step actions executor.
 *
 * Executes every action from actions whose name can be found as key among the stepper's
 * children (steps) components.
 *
 * @param {Gaia.AppModule.Spec} module the current module composition object
 * @returns {function(*): function({actions: *, [p: string]: *}): Promise<*>}
 */
export default module => component => async ({ actions, ...args }) => {
  const { stepper } = sequenceComponentFind(component)
  const steps = asObject(stepper[G.CHILDREN])
  // grouping all step actions and obtaining a list with their names
  const actionKeys = Object.keys(actions)
  // looping steps
  return await actionKeys.reduce(async (acc, key) => {
    const step = steps[key]
    // calling step action
    return step ? await actions[key](module)(step)(await acc) : acc
  }, Promise.resolve(args))
}
